// =============================================================================
// ZONE EDITOR 3D - LIDAR MARKER
// Renders at origin - parent group handles positioning
// =============================================================================

import { useMemo } from 'react'
import { Html } from '@react-three/drei'
import { degreesToRadians } from './utils'
import type { WorkspaceConfig } from '../../types'

interface LidarMarkerProps {
  workspace: WorkspaceConfig
  sensorElevation?: number // scene units (meters), height of sensor center from surface
}

export function LidarMarker({ workspace, sensorElevation }: LidarMarkerProps) {
  const rotRad = degreesToRadians(workspace.lidar_rotation)

  // If sensorElevation provided, scale model to fit; otherwise auto-calculate
  const minDim = Math.min(workspace.width, workspace.height)
  const autoRadius = Math.max(0.015, (minDim * 0.02) / 1000)
  const autoHeight = autoRadius * 2.5

  // The sensor body center should be at sensorElevation
  const targetY = sensorElevation ?? (autoHeight * 1.7)
  const sensorRadius = autoRadius
  // Scale the post to reach targetY
  const postHeight = targetY * 0.9 // post goes from 0 to ~90% of sensor center
  const bodyHeight = sensorRadius * 2
  const arrowLength = Math.max(0.05, (minDim * 0.15) / 1000)

  // Direction arrow at mid-height
  const arrowY = targetY * 0.3
  const arrowPoints = useMemo(() => new Float32Array([
    0, arrowY, 0,
    0, arrowY, -arrowLength
  ]), [arrowY, arrowLength])

  return (
    <group rotation={[0, -rotRad, 0]}>
      {/* LIDAR support post */}
      <mesh position={[0, postHeight / 2, 0]}>
        <cylinderGeometry args={[sensorRadius * 0.3, sensorRadius * 0.4, postHeight, 8]} />
        <meshStandardMaterial color="#555" metalness={0.6} roughness={0.3} />
      </mesh>

      {/* Sensor body */}
      <mesh position={[0, targetY, 0]}>
        <cylinderGeometry args={[sensorRadius * 0.7, sensorRadius, bodyHeight, 16]} />
        <meshStandardMaterial color="#14b8a6" metalness={0.3} roughness={0.4} />
      </mesh>

      {/* Sensor top (rotating part) */}
      <mesh position={[0, targetY + bodyHeight * 0.6, 0]}>
        <sphereGeometry args={[sensorRadius * 0.5, 16, 8]} />
        <meshStandardMaterial color="#eee" metalness={0.5} roughness={0.3} />
      </mesh>

      {/* Direction arrow line */}
      <line>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            array={arrowPoints}
            count={2}
            itemSize={3}
          />
        </bufferGeometry>
        <lineBasicMaterial color="#14b8a6" />
      </line>

      {/* Arrow head */}
      <mesh position={[0, arrowY, -arrowLength]} rotation={[Math.PI / 2, 0, 0]}>
        <coneGeometry args={[sensorRadius * 0.4, sensorRadius * 1.2, 8]} />
        <meshStandardMaterial color="#14b8a6" />
      </mesh>

      {/* Label */}
      <Html
        position={[0, targetY + bodyHeight * 1.5, 0]}
        center
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        <div style={{
          background: 'rgba(0,0,0,0.75)',
          color: '#14b8a6',
          padding: '2px 8px',
          borderRadius: 4,
          fontSize: 11,
          whiteSpace: 'nowrap',
          fontFamily: 'monospace',
          border: '1px solid rgba(20,184,166,0.4)'
        }}>
          LIDAR {workspace.lidar_rotation}° {workspace.lidar_tilt !== 0 ? `tilt ${workspace.lidar_tilt}°` : ''}
        </div>
      </Html>
    </group>
  )
}
